import { lazy, Suspense, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useLang } from '../i18n/LanguageContext';
import styles from './VideoModal.module.css';

const StreamPlayer = lazy(() => import('./StreamPlayer'));

export default function VideoModal({ isOpen, onClose, title, videoId, videoSrc, youtubeId, vertical }) {
  const { t } = useLang();
  useEffect(() => {
    if (!isOpen) return undefined;

    document.documentElement.classList.add('video-modal-open');

    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);

    return () => {
      document.documentElement.classList.remove('video-modal-open');
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className={styles.overlay}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`${title} video`}
    >
      <div
        className={`${styles.modal} ${vertical ? styles.modalVertical : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className={styles.close} onClick={onClose} aria-label={t('video.close')}>
          ×
        </button>
        <h3 className={styles.title}>{title}</h3>
        <div
          className={`${styles.videoWrap} ${vertical ? styles.videoWrapVertical : ''}`}
          id={`service-video-${videoId}`}
        >
          {videoSrc ? (
            <Suspense fallback={<div className={styles.placeholder}>{t('video.loadingPlayer')}</div>}>
              <StreamPlayer videoSrc={videoSrc} />
            </Suspense>
          ) : youtubeId ? (
            <iframe
              src={`https://www.youtube-nocookie.com/embed/${youtubeId}?autoplay=1&rel=0`}
              title={title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          ) : (
            <div className={styles.placeholder}>
              <p>{t('video.exampleComingSoon')}</p>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
